import { useState, useEffect, useMemo } from 'react';
import { employeeService } from '../services/employeeService';

export const useRoleAssignment = (selectedEmployeeForRole, onAssignmentSuccess) => {
  const [assignmentForm, setAssignmentForm] = useState({ factoryId: '', departmentId: '', roleId: '' });
  const [assignmentStructure, setAssignmentStructure] = useState([]);
  const [assignmentOptionsLoading, setAssignmentOptionsLoading] = useState(false);
  const [assignmentOptionsError, setAssignmentOptionsError] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignmentSuccessMessage, setAssignmentSuccessMessage] = useState(null);
  const [assignmentErrorMessage, setAssignmentErrorMessage] = useState(null);
  const [employeeAssignments, setEmployeeAssignments] = useState(null);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState(null);

  // Cargar opciones para asignación de roles
  const loadFactoryDepartRoles = async () => {
    try {
      setAssignmentOptionsLoading(true);
      setAssignmentOptionsError(null);
      const structure = await employeeService.getFactoryDepartRoles();
      setAssignmentStructure(structure);
    } catch (err) {
      console.error('Error cargando opciones de asignación:', err);
      setAssignmentOptionsError(err?.message || err?.toString() || 'Error al cargar opciones');
      setAssignmentStructure([]);
    } finally {
      setAssignmentOptionsLoading(false);
    }
  };

  // Cargar asignaciones de un empleado
  const loadEmployeeAssignments = async (employeeId) => {
    if (!employeeId) {
      setEmployeeAssignments(null);
      return;
    }
    try {
      setAssignmentsLoading(true);
      setAssignmentsError(null);
      const data = await employeeService.getEmployeeAssignments(employeeId);
      setEmployeeAssignments(data);
    } catch (err) {
      console.error('Error cargando asignaciones del empleado:', err);
      setAssignmentsError(err?.message || err?.toString() || 'Error al cargar las asignaciones');
      setEmployeeAssignments(null);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  // Opciones de asignación derivadas de structure
  const assignmentFactories = useMemo(() => assignmentStructure, [assignmentStructure]);
  const selectedFactoryForAssignment = useMemo(
    () => assignmentFactories.find((f) => String(f.id) === String(assignmentForm.factoryId)),
    [assignmentFactories, assignmentForm.factoryId]
  );
  const assignmentDepartments = useMemo(
    () => (selectedFactoryForAssignment?.departments ?? []),
    [selectedFactoryForAssignment]
  );
  const selectedDepartmentForAssignment = useMemo(
    () => assignmentDepartments.find((d) => String(d.id) === String(assignmentForm.departmentId)),
    [assignmentDepartments, assignmentForm.departmentId]
  );
  const assignmentRoles = useMemo(
    () => (selectedDepartmentForAssignment?.available_roles ?? []),
    [selectedDepartmentForAssignment]
  );

  // Asignar rol: usar el nuevo endpoint que asigna un rol a la vez
  const handleAssignRole = async () => {
    if (!selectedEmployeeForRole?.id || !assignmentForm.factoryId || !assignmentForm.departmentId || !assignmentForm.roleId) return;

    setAssignmentSuccessMessage(null);
    setAssignmentErrorMessage(null);

    const selectedRole = assignmentRoles.find((r) => String(r.id) === String(assignmentForm.roleId));
    if (!selectedRole) {
      setAssignmentErrorMessage('Error: No se encontró el rol seleccionado');
      return;
    }

    const roleName = selectedRole.name || selectedRole.description || String(selectedRole.id);
    const factoryId = parseInt(assignmentForm.factoryId, 10);
    const departmentId = parseInt(assignmentForm.departmentId, 10);
    const isPrimary = false; // Valor por defecto

    try {
      setAssigning(true);
      await employeeService.assignRole(selectedEmployeeForRole.id, factoryId, departmentId, isPrimary, roleName);
      setAssignmentSuccessMessage('Asignación realizada correctamente');
      setAssignmentForm({ factoryId: '', departmentId: '', roleId: '' });

      await loadEmployeeAssignments(selectedEmployeeForRole.id);
      if (onAssignmentSuccess) {
        await onAssignmentSuccess();
      }
      setTimeout(() => setAssignmentSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error al asignar rol:', err);
      setAssignmentErrorMessage(err?.message || err?.toString() || 'Error al asignar rol');
    } finally {
      setAssigning(false);
    }
  };

  // Eliminar rol: usar el nuevo endpoint para eliminar un rol individual
  const handleUnassignRole = async (factoryId, departmentId, roleName) => {
    if (!selectedEmployeeForRole?.id) return;

    setAssignmentSuccessMessage(null);
    setAssignmentErrorMessage(null);

    try {
      setAssigning(true);
      await employeeService.unassignRole(selectedEmployeeForRole.id, factoryId, departmentId, roleName);
      setAssignmentSuccessMessage('Rol eliminado correctamente');
      await loadEmployeeAssignments(selectedEmployeeForRole.id);
      if (onAssignmentSuccess) {
        await onAssignmentSuccess();
      }
      setTimeout(() => setAssignmentSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error al eliminar rol:', err);
      setAssignmentErrorMessage(err?.message || err?.toString() || 'Error al eliminar rol');
    } finally {
      setAssigning(false);
    }
  };

  // Sincronizar roles con Authentik
  const handleSyncToAuthentik = async () => {
    if (!selectedEmployeeForRole?.id) return;

    setAssignmentSuccessMessage(null);
    setAssignmentErrorMessage(null);

    try {
      setAssigning(true);
      await employeeService.syncEmployeeToAuthentik(selectedEmployeeForRole.id);
      setAssignmentSuccessMessage('Roles sincronizados con Authentik correctamente');
      setTimeout(() => setAssignmentSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error al sincronizar con Authentik:', err);
      setAssignmentErrorMessage(err?.message || err?.toString() || 'Error al sincronizar con Authentik');
    } finally {
      setAssigning(false);
    }
  };

  // Cargar opciones de asignación al montar
  useEffect(() => {
    loadFactoryDepartRoles();
  }, []);

  // Cargar asignaciones cuando se selecciona un empleado
  useEffect(() => {
    if (selectedEmployeeForRole?.id) {
      loadEmployeeAssignments(selectedEmployeeForRole.id);
    } else {
      setEmployeeAssignments(null);
    }
  }, [selectedEmployeeForRole?.id]);

  return {
    assignmentForm,
    setAssignmentForm,
    assignmentOptionsLoading,
    assignmentOptionsError,
    assigning,
    assignmentSuccessMessage,
    assignmentErrorMessage,
    employeeAssignments,
    assignmentsLoading,
    assignmentsError,
    assignmentFactories,
    assignmentDepartments,
    assignmentRoles,
    handleAssignRole,
    handleUnassignRole,
    handleSyncToAuthentik,
    loadEmployeeAssignments
  };
};
